import {
  FieldErrors,
  FieldValues,
  Path,
  UseFormRegister,
} from 'react-hook-form';
import {
  AddIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  DeleteIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  Text,
  VStack,
} from '@chakra-ui/react';

import i18n from '../../../utils/i18n';

type EditableNameListProps<
  TFieldValues extends FieldValues,
  TFieldKey extends Path<TFieldValues>
> = {
  label: string;
  listItemLabel: string;
  name: TFieldKey;
  fields: { id: string }[];
  register: UseFormRegister<TFieldValues>;
  errors?: FieldErrors<TFieldValues>;
  append: (val: { name: string }) => void;
  remove: (index: number) => void;
  move: (from: number, to: number) => void;
};

export const EditableNameList = <
  TFieldValues extends FieldValues,
  TFieldKey extends Path<TFieldValues>
>({
  label,
  listItemLabel,
  name,
  fields,
  register,
  errors,
  append,
  remove,
  move,
}: EditableNameListProps<TFieldValues, TFieldKey>) => {
  return (
    <Box>
      <Text mb={2}>{label}</Text>
      <VStack spacing={4} align="stretch">
        {fields.length === 0 ? (
          <Text fontStyle="italic" color="gray.500">
            {i18n.t('evaluationEvent.form.editableNameList.noData', {
              label: label.toLowerCase(),
            })}
          </Text>
        ) : (
          <>
            {fields.map((item, index) => {
              const fieldName = `${name}.${index}.name` as Path<TFieldValues>;
              const fieldArrayErrors = errors?.[name as keyof TFieldValues] as
                | Array<{ name?: { message?: string } }>
                | undefined;
              const error = fieldArrayErrors?.[index]?.name;

              return (
                <HStack key={item.id} align="start" alignItems="center">
                  <FormControl isInvalid={!!error}>
                    <HStack spacing={1}>
                      <InputGroup>
                        <InputLeftAddon>{`${listItemLabel} ${
                          index + 1
                        }`}</InputLeftAddon>
                        <Input
                          {...register(fieldName)}
                          placeholder={i18n.t(
                            'evaluationEvent.form.editableNameList.placeholder',
                            {
                              label: listItemLabel.toLowerCase(),
                            }
                          )}
                        />
                      </InputGroup>
                      <IconButton
                        icon={<ArrowUpIcon />}
                        size="sm"
                        aria-label="Move up"
                        onClick={() => move(index, index - 1)}
                        isDisabled={index === 0}
                      />
                      <IconButton
                        icon={<ArrowDownIcon />}
                        size="sm"
                        aria-label="Move down"
                        onClick={() => move(index, index + 1)}
                        isDisabled={index === fields.length - 1}
                      />
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        aria-label="Delete"
                        onClick={() => remove(index)}
                      />
                    </HStack>
                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                  </FormControl>
                </HStack>
              );
            })}
          </>
        )}

        <Button
          size={'sm'}
          leftIcon={<AddIcon />}
          onClick={() => {
            append({ name: '' });
          }}
          alignSelf="start"
        >
          {i18n.t('evaluationEvent.form.editableNameList.add', {
            label: listItemLabel.toLowerCase(),
          })}
        </Button>
      </VStack>
    </Box>
  );
};
